// Candidate API client for cookie-based authentication
import type {
  MagicLinkRequest,
  MagicLinkResponse,
  MagicLinkVerifyRequest,
  MagicLinkVerifyResponse,
  SessionResponse,
  AuthError,
} from "../types/auth";

import type {
  Company,
  CompanyQueryParams,
  ApiResponse,
  ApplicationSubmissionResponse,
  DraftSaveResponse,
} from "./api";

// Base URL configuration
// Empty string uses Vite proxy (same-origin), avoiding cross-origin cookie issues
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

// Session timeout error class
export class SessionTimeoutError extends Error {
  constructor(message: string = "Session expired") {
    super(message);
    this.name = "SessionTimeoutError";
  }
}

// Session timeout handler type
type SessionTimeoutHandler = () => void;

// Candidate API client class - uses cookies for authentication
class CandidateApiClient {
  private baseUrl: string;
  private sessionTimeoutHandler: SessionTimeoutHandler | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  // Set session timeout handler
  public setSessionTimeoutHandler(handler: SessionTimeoutHandler) {
    this.sessionTimeoutHandler = handler;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Get session token from sessionStorage as fallback for cookie-based auth
    // This helps with proxy/cookie issues in development
    const sessionToken = sessionStorage.getItem('candidate_session_token');

    // Always include credentials for cookie-based auth
    const config: RequestInit = {
      ...options,
      credentials: 'include',
      headers: {
        "Content-Type": "application/json",
        // Add Authorization header as fallback when cookies don't work (e.g., proxy issues)
        ...(sessionToken && { "Authorization": `Bearer ${sessionToken}` }),
        ...options.headers,
      },
    };

    // Don't set Content-Type for FormData, let browser handle it
    const isFormData = options.body instanceof FormData;
    if (isFormData) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { "Content-Type": _, ...otherHeaders } = config.headers as Record<string, string>;
      config.headers = otherHeaders;
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Handle 401 Unauthorized (session timeout)
        if (response.status === 401) {
          let errorMessage = "Session expired";
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            }
          } catch {
            // Ignore JSON parsing errors for error responses
          }

          // Trigger session timeout handler if available
          if (this.sessionTimeoutHandler) {
            this.sessionTimeoutHandler();
          }

          throw new SessionTimeoutError(errorMessage);
        }

        // Handle other HTTP errors
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // Ignore JSON parsing errors for error responses
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Check for authentication errors in successful HTTP responses
      if (
        data &&
        !data.success &&
        data.error &&
        (data.error.includes("Authentication required") ||
          data.error.includes("no valid session found") ||
          data.error.includes("Session expired"))
      ) {
        // Trigger session timeout handler if available
        if (this.sessionTimeoutHandler) {
          this.sessionTimeoutHandler();
        }

        throw new SessionTimeoutError(data.error);
      }

      return data;
    } catch (error) {
      // Re-throw with more user-friendly message for network errors
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error - please check your connection and try again"
        );
      }

      throw error;
    }
  }

  // Authentication API
  async sendMagicLink(
    data: MagicLinkRequest
  ): Promise<MagicLinkResponse | AuthError> {
    return this.request<MagicLinkResponse>("/api/v1/auth/magic-link", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async verifyMagicLink(
    data: MagicLinkVerifyRequest
  ): Promise<MagicLinkVerifyResponse | AuthError> {
    return this.request<MagicLinkVerifyResponse>("/api/v1/auth/verify", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getSession(): Promise<SessionResponse | AuthError> {
    return this.request<SessionResponse>("/api/v1/auth/session");
  }

  async logout(): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      "/api/v1/auth/logout",
      {
        method: "POST",
      }
    );
  }

  // Companies API (for candidate use - no token needed)
  async getCompanies(
    params?: CompanyQueryParams
  ): Promise<ApiResponse<{ companies: Company[]; pagination: any }>> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append("status", params.status);
    if (params?.search) searchParams.append("search", params.search);
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.sortBy) searchParams.append("sortBy", params.sortBy);
    if (params?.sortOrder) searchParams.append("sortOrder", params.sortOrder);
    if (params?.startDate) searchParams.append("startDate", params.startDate);
    if (params?.endDate) searchParams.append("endDate", params.endDate);

    const queryString = searchParams.toString();
    const url = `/api/v1/companies${queryString ? `?${queryString}` : ""}`;

    return this.request<ApiResponse<{ companies: Company[]; pagination: any }>>(url);
  }

  async getCompany(
    id: string
  ): Promise<ApiResponse<{ company: Company }>> {
    return this.request<ApiResponse<{ company: Company }>>(
      `/api/v1/companies/${id}`
    );
  }

  // Application API methods (for candidates)
  async submitApplication(
    data: any
  ): Promise<ApiResponse<ApplicationSubmissionResponse>> {
    // Get application ID from data (if exists)
    let applicationId = data.id || data.applicationId;

    // If no application ID, create a new application first
    if (!applicationId) {
      // Extract company ID from form data
      const companyId = data.selectedCompany || data.company || data.companyId;

      if (!companyId) {
        throw new Error("Company selection is required");
      }

      // Create application (backend will delete any existing draft automatically)
      const createResponse = await this.request<ApiResponse<{ id: string }>>(
        "/api/v1/candidate/applications",
        {
          method: "POST",
          body: JSON.stringify({
            companyId,
            applicationData: data,
          }),
        }
      );

      if (!createResponse.success || !createResponse.data?.id) {
        throw new Error("Failed to create application");
      }

      applicationId = createResponse.data.id;
    }

    // Submit the application
    return this.request<ApiResponse<ApplicationSubmissionResponse>>(
      `/api/v1/candidate/applications/${applicationId}/submit`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  }

  async saveDraft(data: any): Promise<ApiResponse<DraftSaveResponse>> {
    return this.request<ApiResponse<DraftSaveResponse>>(
      "/api/v1/applications/draft",
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  }
}

// Export singleton instance
export const candidateApi = new CandidateApiClient(API_BASE_URL);

// Export convenience functions for candidate operations
export const candidateAuthApi = {
  sendMagicLink: (data: MagicLinkRequest) => candidateApi.sendMagicLink(data),
  verifyMagicLink: (data: MagicLinkVerifyRequest) =>
    candidateApi.verifyMagicLink(data),
  getSession: () => candidateApi.getSession(),
  logout: () => candidateApi.logout(),
};

export const candidateCompaniesApi = {
  getAll: (params?: CompanyQueryParams) => candidateApi.getCompanies(params),
  getById: (id: string) => candidateApi.getCompany(id),
};

export const candidateApplicationsApi = {
  submit: (data: any) => candidateApi.submitApplication(data),
  saveDraft: (data: any) => candidateApi.saveDraft(data),
};